import { imgSrc } from "@/lib/img";
import { useEffect, useRef } from "react";
import { Package, Clock, Flame } from "lucide-react";
import { money } from "@/lib/format";

// ---- Matching pipeline (single source of truth for every POS view) -------------
// Fields searched; adding e.g. supplier_sku later is a one-line change here.
export const SEARCH_FIELDS = ["name", "sku", "barcode", "brand", "model", "variant", "category", "description", "unit"];

const norm = (s) => String(s || "").toLowerCase().replace(/×/g, "x").replace(/[\s_\-/]+/g, "");
const lower = (s) => String(s || "").toLowerCase();

// Bigram similarity for typo tolerance ("aluminuim" ~ "aluminium").
const bigrams = (s) => { const out = []; for (let i = 0; i < s.length - 1; i++) out.push(s.slice(i, i + 2)); return out; };
const similar = (a, b) => {
  if (a.length < 4 || b.length < 4) return 0;
  const A = bigrams(a), B = bigrams(b); const set = new Map();
  A.forEach((g) => set.set(g, (set.get(g) || 0) + 1));
  let hits = 0; B.forEach((g) => { if (set.get(g)) { hits++; set.set(g, set.get(g) - 1); } });
  return (2 * hits) / (A.length + B.length);
};

export const isExactIdentifier = (p, q) => {
  const s = lower(q.trim()); if (!s) return false;
  return lower(p.sku) === s || lower(p.barcode) === s;
};

// Returns 0 when no match; higher = better. Exact SKU/barcode always outranks fuzzy.
export const scoreProduct = (p, q) => {
  const s = lower(q.trim()); if (!s) return 1;
  if (isExactIdentifier(p, s)) return 1000;
  const n = norm(s);
  let best = 0;
  for (const f of SEARCH_FIELDS) {
    const v = p[f]; if (!v) continue;
    const lv = lower(v);
    if (lv === s) best = Math.max(best, 500);
    else if (lv.startsWith(s)) best = Math.max(best, f === "name" ? 300 : 200);
    else if (lv.includes(s)) best = Math.max(best, f === "name" ? 150 : 100);
    else if (n && norm(v).includes(n)) best = Math.max(best, 120);
  }
  if (best) return best;
  // Typo tolerance: alpha words fuzzy-match a product word; numeric/size tokens (36x48, 1/2)
  // must appear literally in the normalized product text so sizes stay exact.
  const collapsed = s.replace(/(\d+)\s*[x×]\s*(\d+)/g, "$1x$2");
  const toks = collapsed.split(/\s+/).filter(Boolean);
  if (!toks.length) return 0;
  const hayText = SEARCH_FIELDS.map((f) => lower(p[f])).join(" ");
  const hayNorm = norm(hayText);
  const hayWords = hayText.split(/[^a-z0-9]+/).filter(Boolean);
  const ok = toks.every((w) => /\d/.test(w) || w.length < 4
    ? hayNorm.includes(norm(w))
    : hayWords.some((h) => h.includes(w) || similar(w, h) >= 0.6));
  return ok ? 10 : 0;
};

export const searchProducts = (products, q) => {
  if (!q.trim()) return products;
  return products.map((p) => [scoreProduct(p, q), p]).filter(([sc]) => sc > 0)
    .sort((a, b) => b[0] - a[0] || a[1].name.localeCompare(b[1].name)).map(([, p]) => p);
};
export const productMatches = (p, q) => scoreProduct(p, q) > 0;

// ---- Display helpers ---------------------------------------------------------------
export const availabilityText = (p) => {
  if (p.track_inventory === false) return "Non-Stock · Available to Order";
  if (p.quantity <= 0) return "OUT OF STOCK";
  return `${p.quantity} Available`;
};
const unitLabel = (u) => (u ? u.charAt(0).toUpperCase() + u.slice(1) : "");

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// Highlight the matched text without altering the underlying string. Size queries such
// as "36 x 48" / "36×48" also highlight "36x48" in the product text.
export function Highlight({ text, query }) {
  const t = String(text ?? ""); const q = (query || "").trim();
  if (!q || !t) return t;
  const parts = [esc(q)];
  const sizeRe = q.toLowerCase().replace(/×/g, "x").match(/(\d+)\s*x\s*(\d+)/g) || [];
  sizeRe.forEach((m) => { const [a, b] = m.split(/\s*x\s*/); parts.push(`${esc(a)}[\\s]*[x×][\\s]*${esc(b)}`); });
  q.split(/\s+/).filter((w) => w.length >= 3 && !/\d/.test(w)).forEach((w) => parts.push(esc(w)));
  let re; try { re = new RegExp(`(${parts.join("|")})`, "ig"); } catch { return t; }
  // split() with a capturing group puts matches at odd indices.
  return t.split(re).map((seg, i) => (i % 2 === 1 && seg) ? <mark key={i} className="bg-amber-300/60 dark:bg-amber-400/40 text-inherit rounded-sm px-0.5">{seg}</mark> : seg);
}

const COLS = "grid-cols-[1fr_140px_150px] md:grid-cols-[minmax(0,1fr)_170px_150px_170px]";
const COLS_WIDE = "grid-cols-[minmax(0,1fr)_120px_120px_130px] 2xl:grid-cols-[minmax(0,1fr)_150px_140px_70px_130px_140px]";

function ResultRow({ p, query, active, onPick, large, testPrefix, onHover, wide = false, allowOutOfStock = false }) {
  const ref = useRef(null);
  useEffect(() => { if (active) ref.current?.scrollIntoView({ block: "nearest" }); }, [active]);
  const nonStock = p.track_inventory === false; const out = !allowOutOfStock && !nonStock && p.quantity <= 0;
  const attrs = wide ? [p.model && `Model ${p.model}`, p.category].filter(Boolean) : [p.variant, p.model && `Model ${p.model}`, unitLabel(p.unit), p.category].filter(Boolean);
  return (
    <button ref={ref} disabled={out} onMouseDown={(e) => e.preventDefault()} onMouseEnter={onHover} onClick={() => onPick(p)} data-testid={`${testPrefix}-${p.id}`} data-active={active || undefined}
      className={`w-full text-left grid ${wide ? COLS_WIDE : COLS} gap-3 items-center px-3 ${large ? "py-3" : "py-2"} border-b border-border/70 last:border-0 disabled:opacity-40 ${active ? "bg-emerald-500/15 ring-1 ring-inset ring-emerald-500/50" : "hover:bg-secondary/70"}`}>
      <span className="flex items-start gap-2.5 min-w-0">
        {imgSrc(p) ? <img src={imgSrc(p)} alt="" className="w-9 h-9 rounded object-cover shrink-0 mt-0.5" /> : <Package className={`w-5 h-5 text-muted-foreground/40 shrink-0 mt-1 ${wide ? "hidden 2xl:block" : ""}`} />}
        <span className="min-w-0">
          <span className={`block font-semibold leading-snug whitespace-normal ${wide ? "text-sm 2xl:text-[15px]" : `break-words ${large ? "text-lg" : "text-[15px]"}`}`} data-testid={`${testPrefix}-name-${p.id}`}>
            {p.brand && <span className="text-muted-foreground font-medium"><Highlight text={p.brand} query={query} /> </span>}<Highlight text={p.name} query={query} />
          </span>
          <span className={`block text-muted-foreground leading-snug ${large ? "text-sm" : "text-xs"}`} data-testid={`${testPrefix}-attrs-${p.id}`}>
            {wide && <span className="2xl:hidden">{[p.variant, unitLabel(p.unit)].filter(Boolean).map((a, i) => <span key={i}>{i > 0 && " · "}<Highlight text={a} query={query} /></span>)}{attrs.length && (p.variant || p.unit) ? " · " : ""}</span>}
            {attrs.map((a, i) => <span key={i}>{i > 0 && " · "}<Highlight text={a} query={query} /></span>)}
            {!wide && <span className="md:hidden mono">{attrs.length ? " · " : ""}SKU {p.sku || "-"}</span>}
            {nonStock && <span className="ml-1.5 inline-flex items-center rounded px-1.5 py-px text-[10px] font-bold tracking-wide bg-sky-500/15 text-sky-600 dark:text-sky-400 border border-sky-500/30 align-middle" data-testid={`${testPrefix}-nonstock-${p.id}`}>NON-STOCK</span>}
          </span>
        </span>
      </span>
      <span className={`${wide ? "block" : "hidden md:block"} mono text-muted-foreground break-all ${large ? "text-sm" : "text-xs"}`}><Highlight text={p.sku || "-"} query={query} />{p.barcode && p.barcode !== p.sku ? <span className="block text-[10px] opacity-70"><Highlight text={p.barcode} query={query} /></span> : null}</span>
      {wide && <span className={`hidden 2xl:block font-medium ${large ? "text-base" : "text-sm"}`} data-testid={`${testPrefix}-variant-${p.id}`}><Highlight text={p.variant || "-"} query={query} /></span>}
      {wide && <span className={`hidden 2xl:block text-muted-foreground ${large ? "text-sm" : "text-xs"}`} data-testid={`${testPrefix}-unit-${p.id}`}>{unitLabel(p.unit) || "-"}</span>}
      <span className={`mono font-bold text-right text-emerald-600 dark:text-emerald-400 ${large ? "text-lg" : "text-[15px]"}`}>{money(p.price)}</span>
      <span className={`text-right ${large ? "text-sm" : "text-xs"} ${out ? "text-rose-500 font-semibold" : nonStock ? "text-sky-600 dark:text-sky-400" : "text-muted-foreground"}`} data-testid={`${testPrefix}-avail-${p.id}`}>
        <span className="mono">{availabilityText(p)}</span>
        {!nonStock && p.reserved_qty > 0 && <span className="block text-[10px] text-amber-600 dark:text-amber-400">{p.reserved_qty} reserved for pickup</span>}
      </span>
    </button>
  );
}

// Wide, identification-first result rows. `activeIndex` drives keyboard navigation.
// `wide` (Full Cart) lets the panel overlay ~72% of the workspace with dedicated Size/Unit columns.
// `onDismiss`: a press on a non-row area (header, hints, padding) closes the panel at once instead of
// swallowing the click; `dismissOnLeaveTo` (CSS selector) closes it when the pointer moves onto that
// region (the product grid), so cards beneath the panel become clickable with a single click.
export default function ProductSearchResults({ results, query = "", onPick, large = false, testPrefix = "search-result", activeIndex = -1, onHoverIndex, quickPicks = null, wide = false, emptyText = "No products match.", onDismiss, dismissOnLeaveTo, allowOutOfStock = false }) {
  const rows = results.slice(0, 40);
  const showQuick = !query.trim() && quickPicks;
  const panelW = wide ? "left-4 w-[min(72vw,1400px)] max-w-[calc(100%-2rem)] top-[calc(100%-0.5rem)]" : "left-0 right-0 top-full";
  const onDeadZoneDown = (e) => { if (onDismiss && !e.target.closest("button") && e.target.dataset.scroll === undefined) { e.preventDefault(); onDismiss(); } };
  const onLeave = (e) => { if (onDismiss && dismissOnLeaveTo && e.relatedTarget?.closest?.(dismissOnLeaveTo)) onDismiss(); };
  return (
    <div className={`absolute z-40 ${panelW} mt-1 rounded-xl border border-border bg-card shadow-2xl overflow-hidden ${large ? "max-h-[70vh]" : "max-h-[60vh]"}`} data-testid={`${testPrefix}s`} data-wide={wide || undefined} role="listbox"
      onPointerDown={onDeadZoneDown} onPointerLeave={onLeave}>
      {showQuick ? (
        <div className="max-h-[calc(70vh-1rem)] overflow-y-auto" data-testid="quick-picks" data-scroll>
          {[["Recently Sold", quickPicks.recent, Clock, "recent"], ["Frequently Sold", quickPicks.frequent, Flame, "frequent"]].map(([title, list, Icon, key]) => list?.length ? (
            <div key={key}>
              <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary/60 border-b border-border flex items-center gap-1.5"><Icon className="w-3 h-3" /> {title}</p>
              {list.map((p) => <ResultRow key={`${key}-${p.id}`} p={p} query="" active={false} onPick={onPick} large={large} testPrefix={`quick-${key}`} wide={wide} />)}
            </div>
          ) : null)}
          {!quickPicks.recent?.length && !quickPicks.frequent?.length && <p className="px-3 py-4 text-sm text-muted-foreground">No sales yet. Start typing to search.</p>}
        </div>
      ) : (
        <>
          <div className={`grid ${wide ? COLS_WIDE : COLS} gap-3 px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary/60 border-b border-border`}>
            <span>Product</span><span className={wide ? "" : "hidden md:block"}>SKU</span>
            {wide && <span className="hidden 2xl:block">Size / Variant</span>}{wide && <span className="hidden 2xl:block">Unit</span>}
            <span className="text-right">Price</span><span className="text-right">Available</span>
          </div>
          <div className="overflow-y-auto max-h-[calc(70vh-2rem)]" data-scroll>
            {rows.map((p, i) => <ResultRow key={p.id} p={p} query={query} active={i === activeIndex} onPick={onPick} large={large} testPrefix={testPrefix} onHover={() => onHoverIndex?.(i)} wide={wide} allowOutOfStock={allowOutOfStock} />)}
            {rows.length === 0 && <p className="px-3 py-4 text-sm text-muted-foreground">{emptyText}</p>}
            {results.length > rows.length && <p className="px-3 py-2 text-xs text-muted-foreground bg-secondary/40">Showing {rows.length} of {results.length}. Keep typing to narrow down.</p>}
          </div>
          <p className="px-3 py-1 text-[10px] text-muted-foreground border-t border-border hidden md:block">↑ ↓ to move · Enter to add · Esc to close</p>
        </>
      )}
    </div>
  );
}
